import { useEffect, useState } from "react";
import { Lightbulb, Loader2, Mail, Flame, Target } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Switch } from "../ui/switch";

export default function FitTips({ labelledCount }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (labelledCount < 3) return;
    setLoading(true);
    api.get("/insights/fit-tips")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [labelledCount]);

  const toggleEmail = async (enabled) => {
    setData((d) => ({ ...d, email_enabled: enabled }));
    try {
      await api.patch("/insights/fit-tips/email", { enabled });
      toast.success(enabled ? "Weekly Fit Tips emails are on" : "Weekly Fit Tips emails are off");
    } catch {
      setData((d) => ({ ...d, email_enabled: !enabled }));
      toast.error("Couldn't update your email setting");
    }
  };

  if (labelledCount < 3) return null;
  const tips = data?.tips;

  return (
    <div className="rounded-xl border border-line border-l-[3px] border-l-kesh bg-surface p-5 mb-7 af-fade-up" data-testid="fit-tips">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-brand" />
          <span className="font-display font-bold">This week's Fit Tips</span>
        </div>
        {data && (
          <label className="flex items-center gap-2 text-xs text-body cursor-pointer">
            <Mail className="w-3.5 h-3.5" /> Email me every Monday
            <Switch data-testid="fit-tips-email-toggle" checked={!!data.email_enabled} onCheckedChange={toggleEmail} />
          </label>
        )}
      </div>
      {loading && !tips ? (
        <div className="flex items-center gap-2 text-sm text-body"><Loader2 className="w-4 h-4 animate-spin" /> Preparing your tips…</div>
      ) : tips ? (
        <>
          {data.streak && <Streak s={data.streak} />}
          <p className="text-sm text-ink font-medium mb-3" data-testid="fit-tips-headline">{tips.headline}</p>
          <ul className="space-y-2">
            {tips.tips.map((t, i) => (
              <li key={i} data-testid={`fit-tips-item-${i}`} className="flex gap-2.5 text-sm text-body">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-body">Couldn't load tips right now. They'll refresh next time you visit.</p>
      )}
    </div>
  );
}

function Streak({ s }) {
  const w = s.this_week;
  const left = w.opened ? Math.max(0, 1 - w.sent) : Math.max(0, 3 - w.sent);
  return (
    <div data-testid="fit-tips-streak" className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-brand/15">
      <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${s.weeks > 0 ? "border-warning/40 bg-warning-soft text-warning" : "border-line text-body"}`}>
        <Flame className="w-4 h-4" /> <span><span data-testid="fit-tips-streak-weeks">{s.weeks}</span>-week streak</span>
      </span>
      <span data-testid="fit-tips-streak-progress" className="text-xs text-body">
        {w.followed ? "This week counts. Nice work!" : left > 0 ? `This week: ${w.sent} sent. Send ${left} more to keep your streak.` : ""}
      </span>
      {w.on_target > 0 && (
        <span data-testid="fit-tips-on-target" className="flex items-center gap-1 text-xs text-brand"><Target className="w-3.5 h-3.5" /> {w.on_target} on target</span>
      )}
    </div>
  );
}
